import { z } from "zod";

// Base / leaf schemas
export const CountrySchema = z.object({
  id: z.number(),
  nameFi: z.string(),
  nameSv: z.string(),
  nameEn: z.string(),
  iso2Abbreviation: z.string(),
  iso3Abbreviation: z.string(),
  isEU: z.boolean(),
});

export const IdentifierTypeSchema = z.object({
  id: z.number(),
  identifierFi: z.string(),
  identifierSv: z.string(),
  identifierEn: z.string(),
});

export const IdentifierSchema = z.object({
  id: z.number().optional(),
  identifierType: IdentifierTypeSchema,
  text: z.string(),
});

export const AddressSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  organizationId: z.number(),
  addressLine: z.string(),
  addressLine2: z.string(),
  city: z.string(),
  postCode: z.string(),
  country: CountrySchema,
});

export const InvoicingInformationSchema = z.object({
  id: z.number(),
  ovtNumber: z.string(),
  operator: z.string(),
  email: z.string(),
  address: AddressSchema,
  invoiceReference: z.string(),
  invoiceLanguage: z.number(),
  companyName: z.string(),
  businessIdentifier: z.string(),
});

export const OrganizationSchema = z.object({
  id: z.number().optional(),
  organizationName: z.string(),
  addresses: z.array(AddressSchema).optional(),
  invoicingInformation: InvoicingInformationSchema.optional(),
});

// Composite schemas
export const CompanySchema = z.object({
  id: z.number(),
  companyName: z.string(),
  contractStartDate: z.string().nullable(),
  contractEndDate: z.string().nullable(),
  country: CountrySchema,
  email: z.string(),
  phone: z.string(),
  identifiers: z.array(IdentifierSchema),
  addresses: z.array(AddressSchema),
  organizations: z.array(OrganizationSchema),
  invoicingInformation: InvoicingInformationSchema,
});

export const UpdateCompanyPayloadSchema = z.object({
  id: z.number(),
  companyName: z.string(),
  contractStartDate: z.string().optional(),
  contractEndDate: z.string().optional(),
  country: CountrySchema,
  email: z.string(),
  phone: z.string(),
  identifiers: z.array(IdentifierSchema),
  addresses: z.array(AddressSchema),
  organizations: z.array(
    OrganizationSchema.extend({
      id: z.number(),
      addresses: z.array(AddressSchema),
      invoicingInformation: InvoicingInformationSchema,
    })
  ),
  invoicingInformation: InvoicingInformationSchema,
});

export const CreateCompanyBodySchema = z.object({
  id: z.number().optional(),
  companyName: z.string(),
  country: CountrySchema,
  identifiers: z.array(IdentifierSchema),
  addresses: z.array(AddressSchema),
  organizations: z.array(OrganizationSchema),
  invoicingInformation: InvoicingInformationSchema.optional(),
});

export const GrantPermissionBodySchema = z.object({
  userId: z.number(),
  permission: z.number(),
  companyId: z.number(),
});

export enum InvoiceLanguageEnum {
  Finnish = 0,
  Swedish = 1,
  English = 2,
}

// Export types derived from schemas (replacing previous interfaces)
export type Country = z.infer<typeof CountrySchema>;
export type IdentifierType = z.infer<typeof IdentifierTypeSchema>;
export type Identifier = z.infer<typeof IdentifierSchema>;
export type Address = z.infer<typeof AddressSchema>;
export type InvoicingInformation = z.infer<typeof InvoicingInformationSchema>;
export type Organization = z.infer<typeof OrganizationSchema>;
export type Company = z.infer<typeof CompanySchema>;
export type UpdateCompanyPayload = z.infer<typeof UpdateCompanyPayloadSchema>;
export type CreateCompanyBody = z.infer<typeof CreateCompanyBodySchema>;
export type GrantPermissionBody = z.infer<typeof GrantPermissionBodySchema>;
